import { memo } from 'react';
import { addDays, isWithinInterval } from 'date-fns';
import { Job } from 'models/Jobs';
import { getLeadCheckInDate, getLeadCheckOutDate } from 'utils/lead/leadUtils';
import { getDayCellCommonClasses } from 'pages/calendar/common/cells/DayCellCommon.styles';
import { ExclamationIconStyled } from 'components/icons/ExclamationIconStyled';
import useFormattedPrice from 'pages/calendar/common/cells/useFormattedPrice';
import { classNames } from 'utils/classNameUtils';
import Tooltip from 'components/tooltip/Tooltip';
import { useTranslation } from 'react-i18next';
import useCalendarBodySelection from 'pages/virtualizedStackedCalendar/components/selection/useCalendarBodySelection';
import { SEASONAL_RULE_COLOR_HEX } from 'models/seasonality/SeasonalRuleColors';
import JobView from '../jobs/JobView';
import { stackedCalendarStyleConstants } from '../../Calendar.constants';
import {
  CalendarLead,
  DayCellData,
  StackedCalendarMode,
} from '../../Calendar.types';
import MoonIcon from './MoonIcon';
import DayCellSelection from './DayCellSelection';
import DayCellLead from './DayCellLead';

const DayCell = memo(
  ({
    dayData,
    dayDate,
    leads,
    jobs,
    mode,
    propertyUid,
    isUnitType,
    isUnit,
  }: {
    dayData: DayCellData;
    dayDate: Date;
    leads: CalendarLead[];
    jobs: Job[];
    mode: StackedCalendarMode;
    propertyUid: string;
    isUnitType?: boolean;
    isUnit?: boolean;
  }) => {
    const { t } = useTranslation();

    const { entry, isWeekend, isEmpty, activeSeasonalRules } = dayData;
    const primarySeasonalRule = activeSeasonalRules?.[0];

    const { pricing, availability } = entry || {
      pricing: undefined,
      availability: {
        minimumStayLength: undefined,
        unavailable: false,
        availableForCheckIn: false,
      },
    };

    const isPricingMode =
      mode === StackedCalendarMode.PRICING ||
      mode === StackedCalendarMode.COMBINED;
    const width = stackedCalendarStyleConstants.daysCellWidth[mode];

    const formattedPrice = useFormattedPrice(
      isPricingMode ? pricing : undefined,
    );
    const { handleMouseEnter } = useCalendarBodySelection();

    const onMouseEnter = () => {
      handleMouseEnter({ date: dayDate, propertyUid });
    };

    const {
      minimumStayLength: currentDayMinStay,
      unbookableNight: isUnbookableNight,
    } = availability;

    const isUnavailable =
      availability.unavailable || !availability.availableForCheckIn;

    const shouldShowCellInfo =
      !isEmpty &&
      isPricingMode &&
      !leads?.some((lead) =>
        // need to add 1 day to allow currentLead to skip checkOut but contain checkIn
        isWithinInterval(addDays(dayDate, 1), {
          start: getLeadCheckInDate(lead),
          end: getLeadCheckOutDate(lead),
        }),
      );

    return (
      <div
        className={classNames({
          'body-day-cell': true,
          [getDayCellCommonClasses(dayDate)]: true,
          [`width-${width}`]: true,
          unavailable: isUnavailable,
          weekend: !!isWeekend,
          'has-seasonal-rule': !!primarySeasonalRule,
        })}
        data-day-date={dayDate.getTime()}
        data-is-unit-type={isUnitType}
        data-is-unit={isUnit}
        data-property-uid={propertyUid}
        data-seasonal-rule-uid={primarySeasonalRule?.ruleUid}
        onMouseEnter={onMouseEnter}
      >
        {primarySeasonalRule && (
          <span
            className="seasonal-rule-bg"
            data-testid={`seasonal-rule-bg-${propertyUid}-${dayDate.getTime()}`}
            style={{
              backgroundColor: `${
                SEASONAL_RULE_COLOR_HEX[primarySeasonalRule.color]
              }33`,
            }}
          />
        )}
        {leads?.map((lead) => (
          <DayCellLead
            key={lead.uid}
            mode={mode}
            dayDate={dayDate}
            lead={lead}
            width={width}
          />
        ))}

        {shouldShowCellInfo && (
          <>
            {(currentDayMinStay || isUnbookableNight) && (
              <span className="day-cell-content-line">
                {isUnbookableNight && (
                  <Tooltip
                    id={`unbookable_tooltip_${propertyUid}_${dayDate.getTime()}`}
                    placement="top"
                    text={t('pageCalendar.tooltip.unbookable')}
                  >
                    <ExclamationIconStyled
                      data-testid={`unbookable_icon_${propertyUid}_${dayDate.getTime()}`}
                    />
                  </Tooltip>
                )}
                {currentDayMinStay && (
                  <>
                    <MoonIcon /> {currentDayMinStay}
                  </>
                )}
              </span>
            )}
            {formattedPrice && (
              <span className="day-cell-content-line">{formattedPrice}</span>
            )}
          </>
        )}

        <DayCellSelection dayDate={dayDate} propertyUid={propertyUid} />
        <JobView isUnbookableCell={isUnbookableNight} mode={mode} jobs={jobs} />
      </div>
    );
  },
);

export default DayCell;
